import React, { useState, useEffect } from 'react';
import { categories, formatGestureForDisplay } from '../../utils/GestureCategories';

const CountList = ({ gestureCounts, totalCount, viewAllUsers }) => {
    const [showAllGestures, setShowAllGestures] = useState(false);
    const [sortAlphabetically, setSortAlphabetically] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Reset showAllGestures when viewAllUsers changes
    useEffect(() => {
        setShowAllGestures(false);
    }, [viewAllUsers]);

    // Function to get safe count value (never negative)
    const getSafeCount = (count) => Math.max(0, count || 0);

    // Function to check if a gesture matches the search query
    const gestureMatchesSearch = (gesture) => {
        if (!searchQuery) return true;
        return gesture.toLowerCase().includes(searchQuery.toLowerCase());
    };

    // Function to check if a gesture should be shown
    const shouldShowGesture = (gesture) => {
        const count = getSafeCount(gestureCounts[gesture]);
        return (showAllGestures || count > 0) && gestureMatchesSearch(gesture);
    };

    // Group gestures by category
    const renderCategory = (category) => {
        let categoryGestures = category.gestures
            .filter(gesture => shouldShowGesture(gesture));

        // Apply alphabetical sorting only if enabled and not for numbers category
        if (sortAlphabetically && category.id !== 'numbers') {
            categoryGestures = [...categoryGestures].sort((a, b) => a.localeCompare(b));
        }

        if (categoryGestures.length === 0) return null;

        return (
            <div key={category.id} className="category-section">
                <h4 className="category-header">{category.label}</h4>
                {categoryGestures.map(gesture => (
                    <div key={gesture} className="count-list-item">
                        <span className="gesture-name">{formatGestureForDisplay(gesture)}</span>
                        <span className="gesture-count">{getSafeCount(gestureCounts[gesture])}</span>
                    </div>
                ))}
            </div>
        );
    };

    // Function to get all word gestures (excluding letters and numbers)
    const getWordGestures = () => {
        const wordCategories = categories.filter(cat => !['letters', 'numbers', 'numberingSystems'].includes(cat.id));
        const allWordGestures = wordCategories.flatMap(cat => 
            cat.gestures.filter(shouldShowGesture)
        );
        return sortAlphabetically ? [...allWordGestures].sort((a, b) => a.localeCompare(b)) : allWordGestures;
    };

    // Check if there are any visible gestures
    const hasVisibleGestures = categories.some(category => 
        category.gestures.some(shouldShowGesture)
    );

    // Get letters and numbers categories
    const lettersCategory = categories.find(cat => cat.id === 'letters');
    const numbersCategory = categories.find(cat => cat.id === 'numbers');
    const numberingSystemsCategory = categories.find(cat => cat.id === 'numberingSystems');

    return (
        <div className="count-list-section">
            <div className="count-list-header">
                <div className="count-list-title-row">
                    <div className="title-and-count">
                        <h3>{viewAllUsers ? "All Users' Gestures" : "My Gestures"}</h3>
                        <div className="total-count">
                            <span>Total Recordings: </span>
                            <strong>{getSafeCount(totalCount)}</strong>
                        </div>
                    </div>
                </div>
                <div className="filter-search">
                    <input
                        type="text"
                        placeholder="Search gestures..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="filter-search-input"
                    />
                    {searchQuery && (
                        <button
                            className="filter-search-clear"
                            onClick={() => setSearchQuery('')}
                            title="Clear search"
                        >
                            <img src="/icons/x.svg" alt="Clear" className="icon" />
                        </button>
                    )}
                </div>
                <div className="list-controls">
                    <div className="show-all-toggle">
                        <label>
                            <input
                                type="checkbox"
                                checked={showAllGestures}
                                onChange={(e) => setShowAllGestures(e.target.checked)}
                            />
                            <span className="toggle-text">Show All Gestures</span>
                        </label>
                    </div>
                    <div className="sort-toggle">
                        <label>
                            <input
                                type="checkbox"
                                checked={sortAlphabetically}
                                onChange={(e) => setSortAlphabetically(e.target.checked)}
                            />
                            <span className="toggle-text">Sort A → Z</span>
                        </label>
                    </div>
                </div>
            </div>
            <div className="count-list-container">
                {sortAlphabetically ? (
                    <>
                        {getWordGestures().length > 0 && (
                            <div className="category-section">
                                <h4 className="category-header">Words</h4>
                                {getWordGestures().map(gesture => (
                                    <div key={gesture} className="count-list-item">
                                        <span className="gesture-name">{formatGestureForDisplay(gesture)}</span>
                                        <span className="gesture-count">{getSafeCount(gestureCounts[gesture])}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {lettersCategory && renderCategory(lettersCategory)}
                        {numbersCategory && renderCategory(numbersCategory)}
                        {numberingSystemsCategory && renderCategory(numberingSystemsCategory)}
                    </>
                ) : (
                    <>
                        {categories
                            .filter(cat => !['letters', 'numbers', 'numberingSystems'].includes(cat.id))
                            .map(renderCategory)}
                        {lettersCategory && renderCategory(lettersCategory)}
                        {numbersCategory && renderCategory(numbersCategory)}
                        {numberingSystemsCategory && renderCategory(numberingSystemsCategory)}
                    </>
                )}
                
                {!hasVisibleGestures && (
                    <div className="no-counts-message">
                        {searchQuery ? 'No matching gestures found' : 'No recordings yet'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CountList; 